/*
 * Mail its author styled, in the dark theme (DESIGN.md, the Token Pair Rule). Plain mail already reads
 * in the theme (`withoutNeutralColours`). Styled mail keeps its own light page in light mode; in dark
 * mode its colours are rewritten as Apple Mail and Outlook do: grounds go dark, dark text goes light,
 * brand colours keep their hue and move until they read, and pictures are not touched. The rep can
 * still see the author's page: "Show original" in `mail-frame.tsx`.
 *
 * Contrast holds by construction, not by tracking which ground each word sits on. Every ground this
 * writes is at most as light as the lightest ground the theme's `muted` ink reads on at 4.5:1, and
 * every text colour is at least as light as `muted`. So any text colour reads at WCAG AA on any ground
 * here, an inner cell's ground under inherited text included, and so do the frame's own ink, link and
 * quote colours. Lightness moves in OKLCH, which keeps hue and the steps between shades; a neutral
 * colour takes the desk's warmth from the token it stands in for, so white ground becomes `sheet` and
 * black text `ink`.
 *
 * Safety: this runs on the sanitizer's output only (`sanitizeInboundEmailHtml`), like
 * `withoutNeutralColours`, and changes nothing but colour. A colour declaration or attribute is
 * rebuilt from what was parsed out of it: a hex number, a keyword from a fixed list, a length printed
 * again from its number. One holding anything else is dropped, so the frame's default applies. Every
 * other declaration and attribute is kept as written; the sandbox and policy do not change.
 */
import { FRAME_TOKENS } from "./email-frame.ts";
import {
  ATTRIBUTE,
  BORDER_WORDS,
  DECLARATION_END,
  DECORATION_WORDS,
  entityText,
  NO_BACKGROUND,
  NO_COLOUR,
  TAG,
} from "./email-styling.ts";

type Rgb = readonly [number, number, number];
type Lch = { readonly l: number; readonly c: number; readonly h: number };
/** What a colour does: the ground under text, the text itself, or a rule drawn round a box. */
type Role = "ground" | "ink" | "rule";
type Colour = { readonly rgb: Rgb; readonly alpha: number };

/** CSS's named colours, and the two system colours classic Outlook writes. */
const NAMED_HEX =
  "aliceblue:f0f8ff antiquewhite:faebd7 aqua:00ffff aquamarine:7fffd4 azure:f0ffff beige:f5f5dc " +
  "bisque:ffe4c4 black:000000 blanchedalmond:ffebcd blue:0000ff blueviolet:8a2be2 brown:a52a2a " +
  "burlywood:deb887 cadetblue:5f9ea0 chartreuse:7fff00 chocolate:d2691e coral:ff7f50 " +
  "cornflowerblue:6495ed cornsilk:fff8dc crimson:dc143c cyan:00ffff darkblue:00008b darkcyan:008b8b " +
  "darkgoldenrod:b8860b darkgray:a9a9a9 darkgreen:006400 darkgrey:a9a9a9 darkkhaki:bdb76b " +
  "darkmagenta:8b008b darkolivegreen:556b2f darkorange:ff8c00 darkorchid:9932cc darkred:8b0000 " +
  "darksalmon:e9967a darkseagreen:8fbc8f darkslateblue:483d8b darkslategray:2f4f4f " +
  "darkslategrey:2f4f4f darkturquoise:00ced1 darkviolet:9400d3 deeppink:ff1493 deepskyblue:00bfff " +
  "dimgray:696969 dimgrey:696969 dodgerblue:1e90ff firebrick:b22222 floralwhite:fffaf0 " +
  "forestgreen:228b22 fuchsia:ff00ff gainsboro:dcdcdc ghostwhite:f8f8ff gold:ffd700 " +
  "goldenrod:daa520 gray:808080 green:008000 greenyellow:adff2f grey:808080 honeydew:f0fff0 " +
  "hotpink:ff69b4 indianred:cd5c5c indigo:4b0082 ivory:fffff0 khaki:f0e68c lavender:e6e6fa " +
  "lavenderblush:fff0f5 lawngreen:7cfc00 lemonchiffon:fffacd lightblue:add8e6 lightcoral:f08080 " +
  "lightcyan:e0ffff lightgoldenrodyellow:fafad2 lightgray:d3d3d3 lightgreen:90ee90 " +
  "lightgrey:d3d3d3 lightpink:ffb6c1 lightsalmon:ffa07a lightseagreen:20b2aa lightskyblue:87cefa " +
  "lightslategray:778899 lightslategrey:778899 lightsteelblue:b0c4de lightyellow:ffffe0 " +
  "lime:00ff00 limegreen:32cd32 linen:faf0e6 magenta:ff00ff maroon:800000 mediumaquamarine:66cdaa " +
  "mediumblue:0000cd mediumorchid:ba55d3 mediumpurple:9370db mediumseagreen:3cb371 " +
  "mediumslateblue:7b68ee mediumspringgreen:00fa9a mediumturquoise:48d1cc mediumvioletred:c71585 " +
  "midnightblue:191970 mintcream:f5fffa mistyrose:ffe4e1 moccasin:ffe4b5 navajowhite:ffdead " +
  "navy:000080 oldlace:fdf5e6 olive:808000 olivedrab:6b8e23 orange:ffa500 orangered:ff4500 " +
  "orchid:da70d6 palegoldenrod:eee8aa palegreen:98fb98 paleturquoise:afeeee palevioletred:db7093 " +
  "papayawhip:ffefd5 peachpuff:ffdab9 peru:cd853f pink:ffc0cb plum:dda0dd powderblue:b0e0e6 " +
  "purple:800080 rebeccapurple:663399 red:ff0000 rosybrown:bc8f8f royalblue:4169e1 " +
  "saddlebrown:8b4513 salmon:fa8072 sandybrown:f4a460 seagreen:2e8b57 seashell:fff5ee " +
  "sienna:a0522d silver:c0c0c0 skyblue:87ceeb slateblue:6a5acd slategray:708090 slategrey:708090 " +
  "snow:fffafa springgreen:00ff7f steelblue:4682b4 tan:d2b48c teal:008080 thistle:d8bfd8 " +
  "tomato:ff6347 turquoise:40e0d0 violet:ee82ee wheat:f5deb3 white:ffffff whitesmoke:f5f5f5 " +
  "yellow:ffff00 yellowgreen:9acd32 windowtext:000000 window:ffffff";

function hexRgb(hex: string): Rgb | null {
  if (!/^(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(hex)) return null;
  const short = hex.length <= 4;
  const at = (index: number) =>
    short
      ? Number.parseInt(`${hex[index]}${hex[index]}`, 16)
      : Number.parseInt(hex.slice(index * 2, index * 2 + 2), 16);
  return [at(0), at(1), at(2)];
}

const NAMED = new Map(
  NAMED_HEX.split(" ").flatMap((pair) => {
    const [name = "", hex = ""] = pair.split(":");
    const rgb = hexRgb(hex);
    return rgb ? [[name, rgb] as const] : [];
  }),
);

const NUMBER = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/i;
const clamp = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value));

/** A number, or a percentage of `whole`; null for anything else. */
function amount(text: string, whole: number): number | null {
  const percent = text.endsWith("%");
  const number = percent ? text.slice(0, -1) : text;
  const value = NUMBER.test(number) ? Number(number) : Number.NaN;
  if (!Number.isFinite(value)) return null;
  return percent ? (value / 100) * whole : value;
}

function hslRgb(hue: number, saturation: number, lightness: number): Rgb {
  const k = (n: number) => (n + hue / 30) % 12;
  const a = saturation * Math.min(lightness, 1 - lightness);
  const f = (n: number) => 255 * (lightness - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1)));
  return [f(0), f(8), f(4)];
}

function hexColour(word: string): Colour | null {
  const rgb = hexRgb(word.slice(1));
  const alpha = word.length === 5 ? word[4]?.repeat(2) : word.slice(7, 9);
  return rgb ? { rgb, alpha: alpha ? Number.parseInt(alpha, 16) / 255 : 1 } : null;
}

function rgbChannels([r = "", g = "", b = ""]: readonly string[]): Rgb | null {
  const [red, green, blue] = [amount(r, 255), amount(g, 255), amount(b, 255)];
  if (red === null || green === null || blue === null) return null;
  return [clamp(red, 0, 255), clamp(green, 0, 255), clamp(blue, 0, 255)];
}

function hslChannels([h = "", s = "", l = ""]: readonly string[]): Rgb | null {
  const hue = amount(h.replace(/deg$/, ""), 1);
  const saturation = s.endsWith("%") ? amount(s, 1) : null;
  const lightness = l.endsWith("%") ? amount(l, 1) : null;
  if (hue === null || saturation === null || lightness === null) return null;
  return hslRgb(((hue % 360) + 360) % 360, clamp(saturation, 0, 1), clamp(lightness, 0, 1));
}

/** `rgb()` and `hsl()`, commas or spaces, with or without an alpha. */
function functionColour(word: string): Colour | null {
  const call = /^(rgb|hsl)a?\(([^()]*)\)$/.exec(word);
  const parts = (call?.[2] ?? "").split(/\s*[,/]\s*|\s+/).filter(Boolean);
  if (!call || parts.length < 3 || parts.length > 4) return null;
  const alpha = parts[3] === undefined ? 1 : amount(parts[3], 1);
  const rgb = call[1] === "rgb" ? rgbChannels(parts) : hslChannels(parts);
  return rgb && alpha !== null ? { rgb, alpha: clamp(alpha, 0, 1) } : null;
}

/** A CSS colour this can read, or null: one it cannot is dropped, never guessed. */
function parseColour(token: string): Colour | null {
  const word = token.toLowerCase();
  if (word === "transparent") return { rgb: [0, 0, 0], alpha: 0 };
  const named = NAMED.get(word);
  if (named) return { rgb: named, alpha: 1 };
  return word.startsWith("#") ? hexColour(word) : functionColour(word);
}

/** A legacy `color` or `bgcolor` attribute: a name or hex, with or without its `#`. */
function parseAttributeColour(value: string): Colour | null {
  const word = value.trim();
  if (/^[0-9a-f]{6}$/i.test(word)) return parseColour(`#${word}`);
  return /^(?:#[0-9a-f]{3}|#[0-9a-f]{6}|[a-z]+)$/i.test(word) ? parseColour(word) : null;
}

// OKLab, Björn Ottosson's matrices, on linear sRGB.
const linear = (channel: number) => {
  const value = channel / 255;
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
};
const encoded = (value: number) =>
  255 * (value <= 0.0031308 ? 12.92 * value : 1.055 * value ** (1 / 2.4) - 0.055);

/** WCAG's relative luminance. */
function luminance(rgb: Rgb): number {
  return 0.2126 * linear(rgb[0]) + 0.7152 * linear(rgb[1]) + 0.0722 * linear(rgb[2]);
}

function lchOf(rgb: Rgb): Lch {
  const [r, g, b] = [linear(rgb[0]), linear(rgb[1]), linear(rgb[2])];
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return {
    l: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    c: Math.hypot(a, bb),
    h: Math.atan2(bb, a),
  };
}

function linearOf(lch: Lch): Rgb {
  const a = lch.c * Math.cos(lch.h);
  const b = lch.c * Math.sin(lch.h);
  const l = (lch.l + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (lch.l - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (lch.l - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

const fits = (channels: Rgb) => channels.every((value) => value >= -1e-4 && value <= 1 + 1e-4);

/** The colour at this lightness and hue, its chroma reduced until sRGB can show it. */
function rgbOf(lch: Lch): Rgb {
  let channels = linearOf(lch);
  if (!fits(channels)) {
    let [low, high] = [0, lch.c];
    for (let step = 0; step < 20; step += 1) {
      const middle = (low + high) / 2;
      if (fits(linearOf({ ...lch, c: middle }))) low = middle;
      else high = middle;
    }
    channels = linearOf({ ...lch, c: low });
  }
  const byte = (value: number) => Math.round(clamp(encoded(clamp(value, 0, 1)), 0, 255));
  return [byte(channels[0]), byte(channels[1]), byte(channels[2])];
}

/**
 * The colour nearest `lch` that passes `ok`, moving only its lightness, toward black (0) or white (1),
 * both of which pass. Tested on the rounded colour that is written, so rounding cannot break it.
 */
function settle(lch: Lch, ok: (rgb: Rgb) => boolean, toward: 0 | 1): Rgb {
  const start = rgbOf(lch);
  if (ok(start)) return start;
  let failing = lch.l;
  let passing: number = toward;
  for (let step = 0; step < 24; step += 1) {
    const middle = (failing + passing) / 2;
    if (ok(rgbOf({ ...lch, l: middle }))) passing = middle;
    else failing = middle;
  }
  return rgbOf({ ...lch, l: passing });
}

const tokenLch = (hex: string): Lch => lchOf(hexRgb(hex.slice(1)) ?? [0, 0, 0]);
/** Where a neutral colour of each role lands, and the warmth it takes. */
const DESK: Readonly<Record<Role, Lch>> = {
  ground: tokenLch(FRAME_TOKENS.sheet[1]),
  ink: tokenLch(FRAME_TOKENS.ink[1]),
  rule: tokenLch(FRAME_TOKENS.line[1]),
};
/** Every text colour is at least as light as the theme's dimmest reading ink. */
const INK_FLOOR = luminance(hexRgb(FRAME_TOKENS.muted[1].slice(1)) ?? [0, 0, 0]);
/** Every ground is dark enough for that ink at 4.5:1. */
const GROUND_CEILING = (INK_FLOOR + 0.05) / 4.5 - 0.05;
/** Below this chroma a colour reads as grey and takes the desk's warmth. */
const NEUTRAL = 0.02;
/** Lighter than this is a light colour, flipped; darker is kept and only moved if it must be. */
const LIGHT = 0.6;

/** One colour, made for the dark page. Pure: a light ground goes dark, dark text goes light. */
function adapt(colour: Rgb, role: Role): Rgb {
  const lch = lchOf(colour);
  const desk = DESK[role];
  const tone = lch.c < NEUTRAL ? { c: desk.c, h: desk.h } : null;
  if (role === "ground") {
    const light = lch.l >= LIGHT;
    // White lands on `sheet`; lighter steps stay apart, at 0.4 of their distance.
    const l = light ? desk.l + (1 - lch.l) * 0.4 : lch.l;
    const c = tone?.c ?? (light ? Math.min(lch.c, 0.12) * 0.6 : lch.c);
    return settle({ l, c, h: tone?.h ?? lch.h }, (rgb) => luminance(rgb) <= GROUND_CEILING, 0);
  }
  if (role === "ink") {
    // Black lands on `ink`; dark greys stay near it, mid greys drop toward the floor.
    const l = lch.l >= LIGHT ? lch.l : desk.l - (lch.l / LIGHT) ** 2 * (desk.l - LIGHT);
    const lifted = { l, c: tone?.c ?? lch.c, h: tone?.h ?? lch.h };
    return settle(lifted, (rgb) => luminance(rgb) >= INK_FLOOR, 1);
  }
  // A rule keeps its distance from the page: white on white stays unseen, black becomes light.
  const l = DESK.ground.l + (1 - lch.l) * (0.85 - DESK.ground.l);
  return rgbOf({ l, c: tone?.c ?? lch.c * 0.8, h: tone?.h ?? lch.h });
}

const hex = (rgb: Rgb) => `#${rgb.map((value) => value.toString(16).padStart(2, "0")).join("")}`;

const adapted = new Map<string, string>();
/** The written value: a hex number made here, or `transparent`. Never text from the email. */
function adaptedValue(colour: Colour, role: Role): string {
  if (colour.alpha === 0) return "transparent";
  // A see-through colour is read as it shows on the author's white page.
  const over = (value: number) => Math.round(value * colour.alpha + 255 * (1 - colour.alpha));
  const rgb: Rgb = [over(colour.rgb[0]), over(colour.rgb[1]), over(colour.rgb[2])];
  const key = `${role}:${rgb.join(",")}`;
  const known = adapted.get(key);
  if (known) return known;
  if (adapted.size > 4_000) adapted.clear();
  const value = hex(adapt(rgb, role));
  adapted.set(key, value);
  return value;
}

/** The properties the sanitizer keeps that carry colour, and what each colour does. */
const COLOUR_PROPERTIES: ReadonlyMap<string, Role> = new Map([
  ["color", "ink"],
  ["text-decoration", "ink"],
  ["background", "ground"],
  ["background-color", "ground"],
  ...[
    "border",
    "border-color",
    ...["top", "right", "bottom", "left"].flatMap((side) => [
      `border-${side}`,
      `border-${side}-color`,
    ]),
  ].map((property) => [property, "rule"] as const),
]);
const BACKGROUND_WORDS = new Set([
  ...NO_BACKGROUND,
  ..."repeat-x repeat-y space round fixed local top bottom left right center cover contain".split(
    " ",
  ),
  ..."border-box padding-box content-box / ,".split(" "),
]);
const COLOUR_WORDS = new Set([...NO_COLOUR, "transparent"]);
const wordsOf = (property: string): ReadonlySet<string> =>
  property === "color"
    ? COLOUR_WORDS
    : property === "text-decoration"
      ? DECORATION_WORDS
      : property.startsWith("background")
        ? BACKGROUND_WORDS
        : BORDER_WORDS;

const VALUE_TOKEN = /[a-z-]+\([^()]*\)|[^\s,()]+|,|\S/gi;
const LENGTH = /^([+-]?(?:\d+\.?\d*|\.\d+))(px|pt|em|rem|ex|ch|%|in|cm|mm|pc|vh|vw)?$/i;
const IMPORTANT = /!\s*important\s*$/i;

/** The declaration rebuilt from what was read out of it, or null when any part of it is unreadable. */
function rebuilt(property: string, role: Role, value: string): string | null {
  const text = entityText(value).trim();
  const words: string[] = [];
  for (const token of text.replace(IMPORTANT, "").match(VALUE_TOKEN) ?? []) {
    const colour = parseColour(token);
    const word = token.toLowerCase();
    const length = LENGTH.exec(token);
    if (colour) words.push(adaptedValue(colour, role));
    else if (wordsOf(property).has(word)) words.push(word);
    else if (length) words.push(`${Number(length[1])}${(length[2] ?? "").toLowerCase()}`);
    else return null;
  }
  if (words.length === 0) return null;
  return `${property}:${words.join(" ")}${IMPORTANT.test(text) ? " !important" : ""}`;
}

export type DarkEmail = {
  readonly html: string;
  /** Colour declarations and attributes dropped because this could not read them. */
  readonly dropped: number;
};

/** What could not be read while one email was rewritten. */
type Drops = { count: number };

function adaptedDeclaration(part: string, drops: Drops): string[] {
  const colon = part.indexOf(":");
  const property = part.slice(0, colon).trim().toLowerCase();
  const role = colon < 0 ? undefined : COLOUR_PROPERTIES.get(property);
  if (role === undefined) return [part];
  const declaration = rebuilt(property, role, part.slice(colon + 1));
  if (declaration === null) drops.count += 1;
  return declaration === null ? [] : [declaration];
}

const LEGACY_ROLES: ReadonlyMap<string, Role> = new Map([
  ["color", "ink"],
  ["bgcolor", "ground"],
]);

/** One attribute as written, or rewritten when it is `style`, `color` or `bgcolor`. */
function adaptedAttribute(
  attribute: string,
  rawName: string,
  value: string | undefined,
  drops: Drops,
): string {
  const name = rawName.toLowerCase();
  const role = LEGACY_ROLES.get(name);
  if (value === undefined || (role === undefined && name !== "style")) return attribute;
  if (role === undefined) {
    const rest = value
      .split(DECLARATION_END)
      .flatMap((part) => adaptedDeclaration(part, drops))
      .join(";");
    return rest.trim() === "" ? "" : ` style="${rest}"`;
  }
  const colour = parseAttributeColour(entityText(value));
  if (colour === null) drops.count += 1;
  return colour === null ? "" : ` ${name}="${adaptedValue(colour, role)}"`;
}

/** Styled mail with every colour it carries rewritten for the dark page. */
export function darkAdaptedEmail(sanitizedHtml: string): DarkEmail {
  const drops: Drops = { count: 0 };
  const html = sanitizedHtml.replaceAll(
    TAG,
    (tag, closing: string, name: string, attributes: string) =>
      closing
        ? tag
        : `<${name}${attributes.replaceAll(
            ATTRIBUTE,
            (attribute, rawName: string, value?: string) =>
              adaptedAttribute(attribute, rawName, value, drops),
          )}>`,
  );
  return { html, dropped: drops.count };
}
